import { useEffect, useRef, useState } from 'react';
import { Mic, Square, Upload, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';

/** Lo mismo que acepta el servidor por archivo. */
export const MAX_BYTES_AUDIO = 10 * 1024 * 1024;
/** A ~64 kbps, 15 minutos caben de sobra en los 10 MB. */
const MAX_SEGUNDOS = 15 * 60;

export function tiempo(segundos: number): string {
  const m = Math.floor(segundos / 60);
  const s = segundos % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function extension(mime: string): string {
  if (mime.includes('mp4') || mime.includes('m4a') || mime.includes('aac')) return 'm4a';
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('mpeg')) return 'mp3';
  if (mime.includes('wav')) return 'wav';
  return 'webm';
}

function sePuedeGrabar(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia)
  );
}

type Estado =
  | { paso: 'listo' }
  | { paso: 'grabando'; segundos: number }
  | { paso: 'revisar'; audio: Blob; url: string; segundos: number };

/**
 * Graba una nota de voz con el micrófono (MediaRecorder) o sube un audio que
 * ya exista. Si el navegador no graba o la persona no da permiso, lo dice y
 * queda la opción de subir un archivo: nunca se queda sin camino.
 */
export function GrabadorDeAudio({
  onSubir,
  subiendo,
}: {
  onSubir: (audio: Blob, nombre: string) => Promise<unknown>;
  subiendo: boolean;
}) {
  const [estado, setEstado] = useState<Estado>({ paso: 'listo' });
  const [aviso, setAviso] = useState<string | null>(null);
  const grabadora = useRef<MediaRecorder | null>(null);
  const microfono = useRef<MediaStream | null>(null);
  const reloj = useRef<number | null>(null);
  const archivo = useRef<HTMLInputElement | null>(null);

  const soltarMicrofono = () => {
    if (reloj.current !== null) window.clearInterval(reloj.current);
    reloj.current = null;
    microfono.current?.getTracks().forEach((t) => t.stop());
    microfono.current = null;
  };

  // Al salir de la pantalla: micrófono apagado y la vista previa liberada.
  useEffect(() => () => soltarMicrofono(), []);
  const urlPrevia = estado.paso === 'revisar' ? estado.url : null;
  useEffect(() => () => {
    if (urlPrevia) URL.revokeObjectURL(urlPrevia);
  }, [urlPrevia]);

  const empezar = async () => {
    setAviso(null);
    if (!sePuedeGrabar()) {
      setAviso('Este navegador no deja grabar. Sube un audio que ya tengas (por ejemplo, una nota de voz).');
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const nombre = (err as { name?: string }).name;
      setAviso(
        nombre === 'NotAllowedError' || nombre === 'SecurityError'
          ? 'No nos diste permiso para usar el micrófono. Puedes permitirlo en el candado de la barra de direcciones, o subir un audio que ya tengas.'
          : 'No encontramos un micrófono que funcione. Puedes subir un audio que ya tengas.',
      );
      return;
    }
    microfono.current = stream;
    const pedazos: Blob[] = [];
    let rec: MediaRecorder;
    try {
      rec = new MediaRecorder(stream, { audioBitsPerSecond: 64_000 });
    } catch {
      soltarMicrofono();
      setAviso('Este navegador no deja grabar. Sube un audio que ya tengas.');
      return;
    }
    grabadora.current = rec;
    const inicio = Date.now();
    rec.ondataavailable = (e) => {
      if (e.data.size > 0) pedazos.push(e.data);
    };
    rec.onstop = () => {
      const segundos = Math.round((Date.now() - inicio) / 1000);
      soltarMicrofono();
      const audio = new Blob(pedazos, { type: rec.mimeType || 'audio/webm' });
      if (audio.size === 0) {
        setEstado({ paso: 'listo' });
        setAviso('La grabación salió vacía. Prueba otra vez.');
        return;
      }
      setEstado({ paso: 'revisar', audio, url: URL.createObjectURL(audio), segundos });
    };
    rec.start(1000);
    setEstado({ paso: 'grabando', segundos: 0 });
    reloj.current = window.setInterval(() => {
      const s = Math.round((Date.now() - inicio) / 1000);
      if (s >= MAX_SEGUNDOS && rec.state === 'recording') rec.stop();
      setEstado((e) => (e.paso === 'grabando' ? { paso: 'grabando', segundos: s } : e));
    }, 500);
  };

  const detener = () => {
    if (grabadora.current?.state === 'recording') grabadora.current.stop();
  };

  const subir = async (audio: Blob, nombre: string) => {
    setAviso(null);
    if (audio.size > MAX_BYTES_AUDIO) {
      setAviso('Ese audio pasa de 10 MB. Graba uno más corto o divídelo en varios.');
      return;
    }
    try {
      await onSubir(audio, nombre);
      setEstado({ paso: 'listo' });
    } catch {
      // El aviso del error lo da quien sube; aquí solo se conserva la grabación.
    }
  };

  const elegirArchivo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) void subir(f, f.name);
  };

  return (
    <div className="space-y-3">
      {estado.paso === 'listo' && (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" onClick={() => void empezar()} disabled={subiendo}>
            <Mic className="h-4 w-4" /> Grabar un audio
          </Button>
          <Button type="button" variant="outline" onClick={() => archivo.current?.click()} loading={subiendo}>
            <Upload className="h-4 w-4" /> Subir un audio
          </Button>
        </div>
      )}

      {estado.paso === 'grabando' && (
        <div className="flex items-center gap-3 rounded-md bg-red-50 px-3 py-2 dark:bg-red-900/20">
          <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" aria-hidden />
          <span className="text-sm font-medium tabular-nums text-red-700 dark:text-red-300" aria-live="polite">
            Grabando {tiempo(estado.segundos)}
          </span>
          <Button type="button" variant="danger" size="sm" className="ml-auto" onClick={detener}>
            <Square className="h-3.5 w-3.5" /> Detener
          </Button>
        </div>
      )}

      {estado.paso === 'revisar' && (
        <div className="space-y-2 rounded-md border border-surface-border p-3 dark:border-slate-700">
          <p className="text-sm text-slate-600 dark:text-slate-300">Escúchalo antes de mandarlo ({tiempo(estado.segundos)}).</p>
          <audio controls src={estado.url} className="w-full" />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              loading={subiendo}
              onClick={() => void subir(estado.audio, `grabacion.${extension(estado.audio.type)}`)}
            >
              <Upload className="h-4 w-4" /> Mandar este audio
            </Button>
            <Button type="button" variant="ghost" disabled={subiendo} onClick={() => setEstado({ paso: 'listo' })}>
              <Trash2 className="h-4 w-4" /> Descartar
            </Button>
          </div>
        </div>
      )}

      <input ref={archivo} type="file" accept="audio/*" className="hidden" onChange={elegirArchivo} />

      {aviso && (
        <p role="alert" className="text-sm text-amber-700 dark:text-amber-300">
          {aviso}
        </p>
      )}
    </div>
  );
}
